import { AppButton, AppInput } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@9b3kuq5phwuk4txw/kiban-react-ui/primitives'
import { Search, X } from 'lucide-react'
import { useCallback, useState } from 'react'

interface CustomSearchProps {
  onSearch: (query: string, exclude: string) => void
  onClear: () => void
}

export function CustomSearch({ onSearch, onClear }: CustomSearchProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [exclude, setExclude] = useState('')
  const [activeSearch, setActiveSearch] = useState<{ query: string; exclude: string } | null>(null)

  // 検索実行
  const handleSearch = useCallback(() => {
    onSearch(query, exclude)
    setActiveSearch({ query, exclude })
    setOpen(false)
  }, [query, exclude, onSearch])

  // 検索クリア
  const handleClear = useCallback(() => {
    setQuery('')
    setExclude('')
    setActiveSearch(null)
    onClear()
  }, [onClear])

  // 検索が有効か
  const isSearchEnabled = query.trim() !== '' || exclude.trim() !== ''

  return (
    <div className='flex items-center gap-2'>
      {/* アクティブな検索条件の表示 */}
      {activeSearch && (activeSearch.query || activeSearch.exclude) && (
        <div className='flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-sm'>
          <span className='text-blue-700'>
            {activeSearch.query && `含む: ${activeSearch.query}`}
            {activeSearch.query && activeSearch.exclude && ' / '}
            {activeSearch.exclude && `含まない: ${activeSearch.exclude}`}
          </span>
          <button
            type='button'
            onClick={handleClear}
            className='ml-1 p-0.5 hover:bg-blue-100 rounded'
            aria-label='検索条件をクリア'
          >
            <X className='w-3 h-3 text-blue-500' />
          </button>
        </div>
      )}

      {/* 詳細検索ボタン */}
      <Popover open={open} onOpenChange={setOpen}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type='button'
                  className='p-2 hover:bg-gray-100 rounded transition-colors border'
                  aria-label='詳細検索'
                >
                  <Search className='w-4 h-4' />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>詳細検索（含む/含まない）</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <PopoverContent className='w-[500px]' align='start'>
          <div className='space-y-4'>
            <div className='font-medium text-lg'>詳細検索</div>

            <div className='space-y-3'>
              <AppInput
                id='query'
                label='含む（複数指定時は半角スペース区切り）'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && isSearchEnabled && handleSearch()}
                placeholder='検索キーワード'
              />

              <AppInput
                id='exclude'
                label='含まない（複数指定時は半角スペース区切り）'
                value={exclude}
                onChange={(e) => setExclude(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && isSearchEnabled && handleSearch()}
                placeholder='除外キーワード'
              />
            </div>

            <div className='flex justify-end gap-2 pt-2 border-t'>
              <AppButton size='sm' variant='ghost' onClick={() => setOpen(false)}>
                キャンセル
              </AppButton>
              <AppButton size='sm' variant='default' onClick={handleSearch} disabled={!isSearchEnabled}>
                検索
              </AppButton>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

/**
 * カスタム検索フィルター関数
 * 「含む」「含まない」の条件でデータをフィルタリング
 */
export function customSearchFilter<T extends Record<string, unknown>>(
  items: T[],
  query: string,
  exclude: string,
  searchableFields: (keyof T)[],
): T[] {
  // 検索条件がなければ全件返す
  if (!query.trim() && !exclude.trim()) return items

  // 検索キーワードを分割（半角スペース区切り）
  const queryTerms = query
    .trim()
    .split(/\s+/)
    .filter((t) => t)
  const excludeTerms = exclude
    .trim()
    .split(/\s+/)
    .filter((t) => t)

  return items.filter((item) => {
    // 検索対象のテキストを結合
    const searchableText = searchableFields
      .map((field) => String(item[field] || ''))
      .join(' ')
      .toLowerCase()

    // 「含む」条件: 全てのキーワードを含む（AND検索）
    // 注: 空配列の場合every()はtrueを返すため、queryが空なら全データがマッチする
    const matchesQuery = queryTerms.every((term) => searchableText.includes(term.toLowerCase()))

    // 「含まない」条件: いずれのキーワードも含まない
    // 注: 空配列の場合every()はtrueを返すため、excludeが空なら全データがマッチする
    const matchesExclude = excludeTerms.every((term) => !searchableText.includes(term.toLowerCase()))

    return matchesQuery && matchesExclude
  })
}
